/**
 * Where the flow is mounted, and how to build a path inside it.
 *
 * PF v2 owns relative sub-routes but has always navigated between them with
 * ABSOLUTE `Path.PURCHASE_FLOW_REVAMP_*` constants. That is only correct while
 * the flow has one mount point. The Propstack embed gives it a second
 * (`/embed/purchase-flow`), and an absolute navigation there would take the
 * iframe out of the embed and into the main app mid-order — so every in-flow
 * navigation resolves its base from the route instead of assuming one.
 *
 * `useMatch` rather than pathname equality, so a Propstack-controlled URL with a
 * trailing slash still resolves the embed base — the same reason
 * `EmbedConfig.context` gives for its own `isEmbed`.
 */

import { useCallback } from 'react'
import { useMatch } from 'react-router-dom'

import { Path } from 'constants/router'

/** A step's own path segment. The route table and every navigation share these,
 *  so a renamed step cannot end up matching one and not the other. */
export const PfStepPath = {
  SELECT_USER: 'select-user',
  SELECT_PROPERTY: 'select-property',
  CATALOGUE: 'catalogue',
  CONFIGURATION: 'configuration',
  INSTRUCTIONS: 'instructions',
  BILLING: 'billing',
  SUMMARY: 'summary',
} as const

export type PfStepPathValue = typeof PfStepPath[keyof typeof PfStepPath]

/**
 * The base the current render is mounted under.
 *
 * @example
 * usePfBasePath()  // '/embed/purchase-flow' inside the iframe
 */
export const usePfBasePath = (): string => {
  const root = usePfRootPath()
  const sessionId = usePfUrlSessionId()
  return sessionId ? pfSessionBase(root, sessionId) : root
}

/** The segment that carries the purchase session in the URL, so a reload can find the order again. */
export const PF_SESSION_SEGMENT = 'session'

/** The flow's mount point, without any session segment. */
export const usePfRootPath = (): string =>
  useMatch(`${Path.EMBED_PURCHASE_FLOW}/*`) ? Path.EMBED_PURCHASE_FLOW : Path.PURCHASE_FLOW_REVAMP

/** The base of a flow URL that names the given session. */
export const pfSessionBase = (root: string, sessionId: string): string => `${root}/${PF_SESSION_SEGMENT}/${sessionId}`

/** The session id the current URL names, if it names one. */
export const usePfUrlSessionId = (): string | undefined => {
  const root = usePfRootPath()
  return useMatch(`${root}/${PF_SESSION_SEGMENT}/:sessionId/*`)?.params.sessionId
}

/**
 * Builds an absolute path to a step under the base the flow entered on.
 *
 * @example
 * const pfPath = usePfPath()
 * navigate(pfPath(PfStepPath.SUMMARY))
 */
export const usePfPath = (): (step?: PfStepPathValue | string) => string => {
  const base = usePfBasePath()

  return useCallback((step) => (step ? `${base}/${step}` : base), [base])
}
